import { useEffect, useState } from 'react'
import { Link } from 'react-router'
import { AssociationCard } from '@/components/patterns/AssociationCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { runWithFeedback, toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { toastDeferredDelete } from '@/lib/toastUndo'
import { addListMembers, listChoices, removeListMembers } from '../lists/api'
import type { ListChoice } from '../lists/types'
import type { CrmListRef } from '../types'

type Row = CrmListRef & { members?: number }

/** What makes the card editable: the contact it belongs to, and what to do once the lists changed. */
export interface ListsEditing {
  personId: string
  onChanged: () => void
}

/**
 * "Lijsten" in the right column (design §2.3, spec-63 E5): the lists a contact is on now, static and dynamic
 * (badge "Dynamisch", no removal). On a contact `editing` adds "+ Toevoegen" (a searchable picker of static,
 * not archived lists) and "Uit lijst halen" per static row, with an undo. On an organisation it stays read-only,
 * with the number of its contact persons per list.
 */
export function ListsCard({ lists, editing }: { lists: Row[]; editing?: ListsEditing }) {
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set())
  const [picking, setPicking] = useState(false)
  const shown = lists.filter((list) => !hidden.has(list.id))

  function takeOff(list: Row) {
    if (!editing) return
    const hide = (on: boolean) =>
      setHidden((current) => {
        const next = new Set(current)
        if (on) next.add(list.id)
        else next.delete(list.id)
        return next
      })
    toastDeferredDelete({
      message: `Uit lijst '${list.name}' gehaald.`,
      hide: () => hide(true),
      restore: () => hide(false),
      commit: async () => {
        await removeListMembers(list.id, [editing.personId])
        editing.onChanged()
      },
      error: 'Uit de lijst halen mislukt.',
    })
  }

  return (
    <AssociationCard
      title="Lijsten"
      items={shown}
      itemKey={(list) => list.id}
      emptyText="Nog op geen lijst."
      onAdd={editing ? () => setPicking((open) => !open) : undefined}
      renderItem={(list) => (
        <div className="flex items-center gap-2 text-sm">
          <span className="min-w-0 flex-1 truncate">
            <Link to={`/relaties/lijsten/${list.id}`} className="font-medium underline-offset-2 hover:underline">
              {list.name}
            </Link>
            {list.members === undefined ? null : <span className="text-muted-foreground"> · {list.members}</span>}
          </span>
          {list.dynamic ? (
            <Badge variant="outline">Dynamisch</Badge>
          ) : editing ? (
            <Button type="button" variant="ghost" size="xs" aria-label={`Uit lijst '${list.name}' halen`} onClick={() => takeOff(list)}>
              Uit lijst halen
            </Button>
          ) : null}
        </div>
      )}
      footer={
        <>
          {editing && picking ? (
            <ListPicker
              exclude={new Set(lists.map((list) => list.id))}
              personId={editing.personId}
              onAdded={() => {
                setPicking(false)
                editing.onChanged()
              }}
            />
          ) : null}
          <Link
            to="/relaties/lijsten"
            className="self-start text-xs font-medium text-primary hover:underline max-md:flex max-md:min-h-11 max-md:items-center"
          >
            Naar Lijsten →
          </Link>
        </>
      }
    />
  )
}

/** Static lists that are not archived and not yet on the card; typing narrows the choice. */
function ListPicker({ exclude, personId, onAdded }: { exclude: ReadonlySet<string>; personId: string; onAdded: () => void }) {
  const [choices, setChoices] = useState<ListChoice[] | null>(null)
  useEffect(() => {
    let cancelled = false
    listChoices().then(
      (found) => {
        if (!cancelled) setChoices(found)
      },
      (caught: unknown) => {
        if (!cancelled) {
          toastFailure(caught, 'Lijsten laden mislukt.')
          setChoices([])
        }
      },
    )
    return () => {
      cancelled = true
    }
  }, [])

  async function add(choice: ListChoice) {
    const result = await runWithFeedback(() => addListMembers(choice.id, { person_ids: [personId] }), { error: 'Toevoegen aan de lijst mislukt.' })
    if (!result) return
    if (result.added === 0 && result.skipped.length > 0) toast.info(result.skipped[0].reason)
    else toast.success(`Toegevoegd aan '${choice.name}'.`)
    onAdded()
  }

  const available = (choices ?? []).filter((choice) => choice.type === 'static' && !choice.archived && !exclude.has(choice.id))
  return (
    <Command className="rounded-lg border border-border" aria-label="Kies een lijst">
      <CommandInput placeholder="Zoek een lijst…" />
      <CommandList>
        <CommandEmpty>{choices === null ? 'Laden…' : 'Geen lijsten gevonden.'}</CommandEmpty>
        <CommandGroup>
          {available.map((choice) => (
            <CommandItem key={choice.id} value={choice.name} onSelect={() => void add(choice)}>
              {choice.name}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </Command>
  )
}
